import type { Chat } from "@/state/chatAtoms";
import { ConfirmDeleteDialog } from "@/components/dialogs/ConfirmDeleteDialog";
import { SidebarUser } from "@/components/SidebarUser";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  LogIn,
  Loader2,
  PinIcon,
  PinOffIcon,
  SearchIcon,
  Sparkles,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { getShortcut } from "@/config/shortcuts";
import { useChatFocus } from "@/contexts/ChatFocusContext";
import { useUser } from "@/hooks/useUser";
import { useSetAtom, useAtomValue } from "jotai";
import { proDialog, authDialog } from "@/state/dialogs";
import { loadingChatIdsAtom } from "@/state/chatAtoms";
import { PATHS } from "@/config/paths";
import { useChats } from "@/hooks/useChats";
import { useChatActions } from "@/hooks/useChatActions";
import { UsageStatistics } from "./tooltips/UsageStatistics";

type ChatItemProps = {
  chat: Chat;
  isActive: boolean;
  isPinned: boolean;
  isLoading: boolean;
  totalChatCount: number;
  onChatClick: (chatId: string) => void;
  onPinClick: (chatId: string) => void;
  onDeleteClick: (chatId: string) => void;
};

const ChatItem = ({
  chat,
  isActive,
  isPinned,
  isLoading,
  onChatClick,
  onPinClick,
  onDeleteClick,
}: ChatItemProps) => (
  <SidebarMenuItem className="group/chat-item relative">
    <Tooltip delayDuration={800}>
      <TooltipTrigger asChild>
        <SidebarMenuButton
          isActive={isActive}
          onClick={() => onChatClick(chat.id)}
          className="w-full"
        >
          <span className="truncate flex-1">{chat.title}</span>
          {isLoading && (
            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-2 flex-shrink-0" />
          )}
        </SidebarMenuButton>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs text-xs px-2 py-1">
        {chat.title}
      </TooltipContent>
    </Tooltip>
    <div className="absolute right-1 top-0 bottom-0 flex gap-1 opacity-0 group-hover/chat-item:opacity-100 transition-all duration-200 translate-x-2 group-hover/chat-item:translate-x-0 bg-sidebar z-10 rounded-md">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onPinClick(chat.id);
            }}
            className={cn(
              "h-full w-6 hover:bg-sidebar-accent-foreground/10",
              isPinned && "text-blue-500",
            )}
          >
            {isPinned ? <PinOffIcon className="h-3 w-3" /> : <PinIcon className="h-3 w-3" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>{isPinned ? "Unpin Chat" : "Pin Chat"}</p>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(chat.id);
            }}
            className="h-full w-6 hover:bg-red-500/10 hover:text-red-500"
          >
            <TrashIcon className="h-3 w-3" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>Delete Chat</p>
        </TooltipContent>
      </Tooltip>
    </div>
  </SidebarMenuItem>
);

type ChatSectionProps = {
  label: string | React.ReactNode;
  chats: Chat[];
  activeChat: string;
  loadingChatIds: Set<string>;
  totalChatCount: number;
  onChatClick: (chatId: string) => void;
  onPinClick: (chatId: string) => void;
  onDeleteClick: (chatId: string) => void;
};

const ChatSection = ({
  label,
  chats,
  activeChat,
  loadingChatIds,
  totalChatCount,
  onChatClick,
  onPinClick,
  onDeleteClick,
}: ChatSectionProps) => {
  if (chats.length === 0) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {chats.map((chat) => (
            <ChatItem
              key={chat.id}
              chat={chat}
              isActive={activeChat === chat.id}
              isPinned={chat.isPinned || false}
              isLoading={loadingChatIds.has(chat.id)}
              totalChatCount={totalChatCount}
              onChatClick={onChatClick}
              onPinClick={onPinClick}
              onDeleteClick={onDeleteClick}
            />
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

// Helper function to group chats by date
const groupChatsByDate = (chats: Chat[]) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);

  return chats.reduce(
    (acc, chat) => {
      const chatDate = new Date(chat.updatedAt);
      const chatDay = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());

      if (chatDay.getTime() === today.getTime()) {
        acc.today.push(chat);
      } else if (chatDay.getTime() === yesterday.getTime()) {
        acc.yesterday.push(chat);
      } else if (chatDay >= lastWeek) {
        acc.lastWeek.push(chat);
      } else {
        acc.older.push(chat);
      }

      return acc;
    },
    { today: [] as Chat[], yesterday: [] as Chat[], lastWeek: [] as Chat[], older: [] as Chat[] },
  );
};

export const AppSidebar = () => {
  const { isMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const currentChatId = params.chatId;
  const { focusChatInput } = useChatFocus();
  const [searchQuery, setSearchQuery] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);

  const setProDialogOpen = useSetAtom(proDialog);
  const setAuthDialogState = useSetAtom(authDialog);
  const loadingChatIds = useAtomValue(loadingChatIdsAtom);

  const { isAuthenticated, hasPro } = useUser();

  // InstantDB queries
  const { chats } = useChats();
  const chatsLoading = false;

  // Chat actions
  const { deleteChat, togglePinChat } = useChatActions({ currentChatId });

  const handlePinChat = (chatId: string) => {
    togglePinChat(chatId);
  };

  const handleDeleteChat = (chatId: string) => {
    setChatToDelete(chatId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteChat = async () => {
    if (chatToDelete) {
      const result = await deleteChat(chatToDelete);

      // Handle navigation after deletion
      if (result) {
        if (result.wasActive) {
          // If we deleted the active chat, navigate appropriately
          if (result.nextChatId) {
            handleChatClick(result.nextChatId);
          } else {
            // No more chats, navigate to home
            navigate({ to: "/" });
          }
        }
      }
    }
    setDeleteDialogOpen(false);
    setChatToDelete(null);
  };

  const filteredChats = (chatList: Chat[]) => {
    if (!searchQuery) return chatList;
    return chatList.filter((chat) => chat.title.toLowerCase().includes(searchQuery.toLowerCase()));
  };

  const getAllPinnedChats = () => {
    return chats.filter((chat) => chat.isPinned);
  };

  const getUnpinnedChats = (chatList: Chat[]) => {
    return chatList.filter((chat) => !chat.isPinned);
  };

  // Group chats by date
  const groupedChats = groupChatsByDate(chats);

  const handleNewChat = () => {
    // Navigate to home route for new chat
    navigate({ to: "/" });
    // Focus the chat input after navigation
    setTimeout(() => {
      focusChatInput();
    }, 100);

    // Close sidebar on mobile after navigation
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleChatClick = (chatId: string) => {
    // Navigate to specific chat route
    navigate({
      to: "/chat/$chatId",
      params: { chatId },
    });

    // Close sidebar on mobile after navigation
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Get chats in the same order they appear in the sidebar (pinned first, then grouped by date)
  const getSidebarOrderedChats = () => {
    const pinnedChats = getAllPinnedChats();
    const unpinnedChats = getUnpinnedChats(chats);

    // Return all chats in sidebar order: pinned first, then unpinned
    return [...pinnedChats, ...unpinnedChats];
  };

  // Keyboard navigation for cycling through chats
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Use CMD/Ctrl for all shortcuts everywhere
      const isModifierPressed = event.metaKey || event.ctrlKey;

      // Get shortcuts for navigation
      const nextChatShortcut = getShortcut("nextChat");
      const prevChatShortcut = getShortcut("prevChat");

      // For navigation shortcuts, check if we're typing in sidebar inputs specifically
      // This allows navigation to work even when chat input is focused
      const isNavigationShortcut =
        event.key === nextChatShortcut.key || event.key === prevChatShortcut.key;
      const isSidebarInput = (event.target as HTMLElement)?.closest("[data-sidebar-input]");
      const shouldBlockNavigation = isNavigationShortcut && isSidebarInput;

      if (isModifierPressed && isNavigationShortcut && !shouldBlockNavigation) {
        event.preventDefault();

        if (chats.length < 1) return;

        // Get chats in sidebar order
        const sidebarChats = getSidebarOrderedChats();

        const currentIndex = sidebarChats.findIndex((chat) => chat.id === currentChatId);
        let nextIndex: number;

        if (event.key === nextChatShortcut.key) {
          nextIndex = currentIndex >= sidebarChats.length - 1 ? 0 : currentIndex + 1;
        } else {
          nextIndex = currentIndex <= 0 ? sidebarChats.length - 1 : currentIndex - 1;
        }

        const nextChat = sidebarChats[nextIndex];
        if (nextChat) {
          handleChatClick(nextChat.id);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [chats, currentChatId, handleChatClick]);

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center justify-center p-3 h-[57px] relative">
          <h1
            className="font-semibold text-lg -mt-3 cursor-pointer hover:opacity-80"
            onClick={() => {
              navigate({ to: PATHS.HOME });
              // Close sidebar on mobile after navigation
              if (isMobile) {
                setOpenMobile(false);
              }
            }}
          >
            FilePrompt
          </h1>
        </div>
        <Button className="w-full justify-center" variant="outline" onClick={handleNewChat}>
          New Chat
        </Button>

        <div className="relative py-2">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <SidebarInput
            placeholder="Search your chats"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9 bg-sidebar-accent border-0"
            data-sidebar-input
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchQuery("")}
              className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 hover:bg-sidebar-accent-foreground/10"
            >
              <XIcon className="h-3 w-3" />
            </Button>
          )}
        </div>

        <SidebarSeparator />
      </SidebarHeader>

      <SidebarContent ref={contentRef}>
        <ChatSection
          label={
            <div className="flex items-center gap-1">
              <PinIcon className="h-3 w-3" />
              Pinned
            </div>
          }
          chats={filteredChats(getAllPinnedChats())}
          activeChat={currentChatId || ""}
          loadingChatIds={loadingChatIds}
          totalChatCount={chats.length}
          onChatClick={handleChatClick}
          onPinClick={handlePinChat}
          onDeleteClick={handleDeleteChat}
        />

        <ChatSection
          label="Today"
          chats={filteredChats(getUnpinnedChats(groupedChats.today))}
          activeChat={currentChatId || ""}
          loadingChatIds={loadingChatIds}
          totalChatCount={chats.length}
          onChatClick={handleChatClick}
          onPinClick={handlePinChat}
          onDeleteClick={handleDeleteChat}
        />

        <ChatSection
          label="Yesterday"
          chats={filteredChats(getUnpinnedChats(groupedChats.yesterday))}
          activeChat={currentChatId || ""}
          loadingChatIds={loadingChatIds}
          totalChatCount={chats.length}
          onChatClick={handleChatClick}
          onPinClick={handlePinChat}
          onDeleteClick={handleDeleteChat}
        />

        <ChatSection
          label="Last 7 days"
          chats={filteredChats(getUnpinnedChats(groupedChats.lastWeek))}
          activeChat={currentChatId || ""}
          loadingChatIds={loadingChatIds}
          totalChatCount={chats.length}
          onChatClick={handleChatClick}
          onPinClick={handlePinChat}
          onDeleteClick={handleDeleteChat}
        />

        <ChatSection
          label="Older"
          chats={filteredChats(getUnpinnedChats(groupedChats.older))}
          activeChat={currentChatId || ""}
          loadingChatIds={loadingChatIds}
          totalChatCount={chats.length}
          onChatClick={handleChatClick}
          onPinClick={handlePinChat}
          onDeleteClick={handleDeleteChat}
        />

        {chatsLoading && (
          <SidebarGroup>
            <SidebarGroupContent>
              <div className="text-center py-8 text-sm text-muted-foreground">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="font-medium">Loading chats...</p>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu className="relative gap-2">
          {isAuthenticated && (
            <>
              {!hasPro && (
                <SidebarMenuItem>
                  <Button
                    onClick={() => setProDialogOpen(true)}
                    className="w-full bg-gradient-to-r from-violet-500/90 to-purple-500/90 hover:from-violet-600 hover:to-purple-600 text-white shadow-sm hover:shadow-md transition-all"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    <span className="font-medium">Upgrade to Pro</span>
                  </Button>
                </SidebarMenuItem>
              )}
            </>
          )}


          <UsageStatistics />
        </SidebarMenu>

        <SidebarUser />

        <div className="flex flex-col gap-2 group-data-[collapsible=icon]:hidden">
          {!isAuthenticated && (
            <Button
              variant="ghost"
              onClick={() => setAuthDialogState({ open: true })}
              className="w-full justify-start py-6 mb-3"
            >
              <LogIn className="h-4 w-4" />
              Sign In
            </Button>
          )}
        </div>
      </SidebarFooter>

      <SidebarRail />

      <ConfirmDeleteDialog
        title="Delete Chat"
        description="Are you sure you want to delete this chat? This action cannot be undone."
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDeleteChat}
      />
    </Sidebar>
  );
};
